// Units: remembered per browser.
export const UNIT_SYSTEM_STORAGE_KEY = "glc.Units";
export const UNIT_SYSTEMS = Object.freeze({
  imperial: { depth: "ft", waveHeight: "ft", waterTemperature: "F", airTemperature: "F", speed: "mph", windSpeed: "mph", distance: "mi" },
  metric: { depth: "m", waveHeight: "m", waterTemperature: "C", airTemperature: "C", speed: "kph", windSpeed: "kph", distance: "km" },
  nautical: { depth: "fm", waveHeight: "ft", waterTemperature: "F", airTemperature: "F", speed: "kn", windSpeed: "kn", distance: "nmi" },
  canadian: { depth: "ft", waveHeight: "ft", waterTemperature: "F", airTemperature: "C", speed: "mph", windSpeed: "kph", distance: "km" }
});
const UNIT_LABELS = { kph: "km/h" };

// Wave-height categories (feet), the same defaults the fishing logbook uses.
export const CHOP_RANGES = Object.freeze([
  { id: "calm", label: "Calm", maxFeet: 0.5 },
  { id: "light", label: "Light Chop", maxFeet: 1 },
  { id: "moderate", label: "Moderate Chop", maxFeet: 1.5 },
  { id: "very-choppy", label: "Very Choppy", maxFeet: 2 },
  { id: "rough", label: "Rough", maxFeet: null }
]);

let unitSystem = readUnitSystem();

function defaultUnitSystem() {
  const preferred = globalThis.document?.body?.dataset.defaultUnits;
  return UNIT_SYSTEMS[preferred] ? preferred : "imperial";
}

function readUnitSystem() {
  try {
    const saved = localStorage.getItem(UNIT_SYSTEM_STORAGE_KEY);
    return UNIT_SYSTEMS[saved] ? saved : defaultUnitSystem();
  } catch { return defaultUnitSystem(); }
}

export function currentUnitSystem() {
  return unitSystem;
}

export function setUnitSystem(system) {
  unitSystem = UNIT_SYSTEMS[system] ? system : "imperial";
  try { localStorage.setItem(UNIT_SYSTEM_STORAGE_KEY, unitSystem); } catch { /* storage unavailable */ }
  document.dispatchEvent(new CustomEvent("glc-units-changed", { detail: { system: unitSystem } }));
}

export function unitPreference(key) {
  return UNIT_SYSTEMS[unitSystem][key] || "";
}

export function unitSymbol(key) {
  const unit = unitPreference(key);
  if (unit === "C" || unit === "F") return `\u00b0${unit}`;
  return UNIT_LABELS[unit] || unit;
}

export function convertUnitValue(value, fromUnit, toUnit) {
  const number = Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(number)) return null;
  if (fromUnit === toUnit) return number;
  if (fromUnit === "C" && toUnit === "F") return (number * 9 / 5) + 32;
  if (fromUnit === "F" && toUnit === "C") return (number - 32) * 5 / 9;
  const conversions = [
    { kph: 1, mph: 1.609344, kn: 1.852 },
    { m: 1, ft: 0.3048, fm: 1.8288, km: 1000, mi: 1609.344, nmi: 1852 }
  ];
  const table = conversions.find((units) => fromUnit in units && toUnit in units);
  return table ? number * table[fromUnit] / table[toUnit] : number;
}

function trimNumber(value) {
  return String(Number(value.toFixed(6)));
}

export function formatUnitValue(value, key, fromUnit, options = {}) {
  const converted = convertUnitValue(value, fromUnit, unitPreference(key));
  if (converted === null) return "—";
  const decimals = options.decimals ?? (Math.abs(converted) < 10 && !Number.isInteger(converted) ? 1 : 0);
  return `${trimNumber(Math.round(converted * (10 ** decimals)) / (10 ** decimals))} ${unitSymbol(key)}`;
}

export function currentChopRanges() {
  return CHOP_RANGES;
}
